/**
 * 日付関連のユーティリティ関数
 */
/**
 * 2つの日付の間の日数を計算する
 * @param startDateString 開始日
 * @param endDateString 終了日
 * @returns 日数（終了日を含む）
 */
export const calculateDaysBetween = (startDateString: string | null | undefined, endDateString: string | null | undefined): number | null => {
  if (!startDateString || !endDateString) {
    return null;
  }
  try {
    const startDate = new Date(startDateString);
    const endDate = new Date(endDateString);
    // 日付部分のみを考慮するために時刻をリセット
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);
    // ミリ秒の差分を計算し、日数に変換（終了日を含むため+1）
    const diffTime = endDate.getTime() - startDate.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays > 0 ? diffDays : null;
  } catch (error) {
    console.error('日数計算エラー:', error);
    return null;
  }
};
/**
 * 終了日までの残り日数を計算し、状態を返す
 * @param endDateString 終了日の文字列
 * @returns 残り日数の状態 { status: '残り' | '最終日' | '終了', days: number | null }
 */
export const getRemainingDaysInfo = (endDateString: string | null | undefined) => {
  if (!endDateString) {
    return { status: null, days: null };
  }
  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // 今日の0時0分に設定
    const endDate = new Date(endDateString);
    endDate.setHours(0, 0, 0, 0); // 終了日の0時0分に設定
    // 日数の差を計算（ミリ秒を日数に変換）
    const diffTime = endDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays > 0) {
      // 残り日数がある場合
      return { status: '残り', days: diffDays };
    } else if (diffDays === 0) {
      // 今日が最終日の場合
      return { status: '最終日', days: 0 };
    } else {
      // 終了日を過ぎている場合
      return { status: '終了', days: diffDays };
    }
  } catch (error) {
    console.error('残り日数計算エラー:', error);
    return { status: null, days: null };
  }
};
/**
 * 標準的な日付フォーマット - 日付のみ (例: 2023年4月11日)
 */
export const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  try {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    return new Date(dateString).toLocaleDateString('ja-JP', options);
  } catch (error) {
    console.error('日付フォーマットエラー:', error);
    return '-';
  }
};
/**
 * 詳細な日付フォーマット - 日付と時刻 (例: 2023/4/11 14:30 または 2023年4月11日 14:30)
 * @param dateString 日付文字列
 * @param format フォーマット形式 ('slash'=スラッシュ区切り, 'jp'=日本語形式)
 */
export const formatDateWithTime = (dateString: string, format: 'slash' | 'jp' = 'jp'): string => {
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return '-';
    }
    const year = date.getFullYear();
    const month = format === 'slash' ? date.getMonth() + 1 : date.getMonth() + 1;
    const day = date.getDate();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    if (format === 'slash') {
      return `${year} / ${month} / ${day} ${hours}:${minutes}`;
    } else {
      const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      };
      return new Date(dateString).toLocaleDateString('ja-JP', options);
    }
  } catch (error) {
    console.error('日付フォーマットエラー:', error);
    return '-';
  }
};
/**
 * ダッシュボード表示用の日時フォーマット (例: 2023/04/11 14:30:25)
 */
export const formatDateTime = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return '-';
    }
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`;
  } catch (error) {
    console.error('日付フォーマットエラー:', error);
    return '-';
  }
};
/**
 * 現在から指定した日付までの経過時間を人間が読みやすい形式で返す
 * 例: "3年2ヶ月前", "5日前", "2時間前", "30分前", "たった今"
 */
export const getHumanReadableTimeAgo = (dateString: string): string => {
  const now = new Date();
  const date = new Date(dateString);
  const diffTime = now.getTime() - date.getTime();
  // 経過時間を計算
  const diffMinutes = Math.floor(diffTime / (1000 * 60));
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);
  if (diffYears > 0) {
    const remainingMonths = Math.floor((diffDays % 365) / 30);
    if (remainingMonths > 0) {
      return `${diffYears}年${remainingMonths}ヶ月前`;
    }
    return `${diffYears}年前`;
  } else if (diffMonths > 0) {
    return `${diffMonths}ヶ月前`;
  } else if (diffDays > 0) {
    return `${diffDays}日前`;
  } else if (diffHours > 0) {
    return `${diffHours}時間前`;
  } else if (diffMinutes > 0) {
    return `${diffMinutes}分前`;
  } else {
    return 'たった今';
  }
};
/**
 * 看護師免許取得年から看護経験年数を計算する
 * @param licenseYear 看護師免許取得年
 * @returns 計算された経験年数と取得年の表示形式
 */
export const calculateNursingExperienceYears = (licenseYear: number): string => {
  const currentYear = new Date().getFullYear();
  const experienceYears = currentYear - licenseYear;
  return `${experienceYears}年 (${licenseYear}年)`;
};
/**
 * Date型をISO形式の日付文字列(yyyy-MM-dd)に変換する
 * HTML inputのdate型で使用する形式
 * @param date 変換する日付
 * @returns yyyy-MM-dd形式の文字列
 */
export const formatDateToISO = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};
